// Per-user favorites (0113) — starred tickets + change orders. A lightweight hook
// each surface uses independently: it loads the caller's favorite ids, exposes an
// optimistic toggle, and a membership test that drives the "Favorites only"
// list filter and the star buttons on detail pages.

import { useCallback, useEffect, useState } from "react";
import { addFavorite, getFavorites, removeFavorite } from "./api";
import type { FavoriteIds, FavoriteType } from "./types";

const EMPTY: FavoriteIds = { tickets: [], changes: [], facilities: [] };

const KEY: Record<FavoriteType, keyof FavoriteIds> = {
  ticket: "tickets",
  change: "changes",
  facility: "facilities",
};

export function useFavorites() {
  const [ids, setIds] = useState<FavoriteIds>(EMPTY);

  const reload = useCallback(() => {
    getFavorites()
      .then(setIds)
      .catch(() => {
        /* not fatal — favorites just stay empty */
      });
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const isFavorite = useCallback(
    (type: FavoriteType, id: string) => ids[KEY[type]].includes(id),
    [ids],
  );

  const toggle = useCallback(
    async (type: FavoriteType, id: string) => {
      const key = KEY[type];
      const on = ids[key].includes(id);
      // Optimistic update; revert by reloading if the write fails.
      setIds((prev) => ({
        ...prev,
        [key]: on ? prev[key].filter((x) => x !== id) : [...prev[key], id],
      }));
      try {
        if (on) await removeFavorite(type, id);
        else await addFavorite(type, id);
      } catch {
        reload();
      }
    },
    [ids, reload],
  );

  return { ids, isFavorite, toggle, reload };
}
